import {
  AudienceFieldType,
  AudienceMatchType,
  AudienceOpType,
  type AudienceConditionInputType,
  type AudienceFilterInputType,
} from "@/types/graphql/partner.generated";
import type { AutomationGraphRulesDetails, MarketingLimits } from "./types";

/**
 * Правила фильтра аудитории рассылки. Структурные проверки повторяют
 * бэкенд (domains/marketing, AUDIENCE_FILTER_INVALID), числовые пределы
 * приходят с бэкенда запросом marketingLimits. Без пределов (null, запрос
 * ещё не ответил) проверяется только заполненность условий.
 */
export const AUDIENCE_FIELDS: AudienceFieldType[] = [
  AudienceFieldType.HasPurchasesType,
  AudienceFieldType.PurchasedProductType,
  AudienceFieldType.SpentUsdType,
  AudienceFieldType.HasActiveSubscriptionType,
  AudienceFieldType.LastViewDaysAgoType,
  AudienceFieldType.RegisteredDaysAgoType,
  AudienceFieldType.CountryType,
  AudienceFieldType.LanguageType,
  AudienceFieldType.RegistrationUtmSourceType,
  AudienceFieldType.RegistrationUtmCampaignType,
  AudienceFieldType.RegistrationUtmMediumType,
  AudienceFieldType.RegistrationLinkType,
  AudienceFieldType.RegistrationEmailCampaignType,
];

/**
 * Поля условия шага CONDITION цепочки (в аудитории рассылок и правилах
 * выхода их нет) из правил вида (AutomationGraphRules.conditionFields);
 * без правил пусто.
 */
export const conditionFieldsOf = (
  rules: Pick<AutomationGraphRulesDetails, "conditionFields"> | null
): AudienceFieldType[] => rules?.conditionFields ?? [];

/** Допустимые операции поля; первая идёт по умолчанию. */
export const AUDIENCE_FIELD_OPS: Record<AudienceFieldType, AudienceOpType[]> = {
  [AudienceFieldType.HasPurchasesType]: [AudienceOpType.IsType],
  [AudienceFieldType.PurchasedProductType]: [
    AudienceOpType.InType,
    AudienceOpType.NotInType,
  ],
  [AudienceFieldType.SpentUsdType]: [
    AudienceOpType.GteType,
    AudienceOpType.LteType,
  ],
  [AudienceFieldType.HasActiveSubscriptionType]: [AudienceOpType.IsType],
  [AudienceFieldType.LastViewDaysAgoType]: [
    AudienceOpType.GteType,
    AudienceOpType.LteType,
    AudienceOpType.NeverType,
  ],
  [AudienceFieldType.RegisteredDaysAgoType]: [
    AudienceOpType.GteType,
    AudienceOpType.LteType,
  ],
  [AudienceFieldType.CountryType]: [
    AudienceOpType.InType,
    AudienceOpType.NotInType,
  ],
  [AudienceFieldType.LanguageType]: [AudienceOpType.InType],
  [AudienceFieldType.RegistrationUtmSourceType]: [AudienceOpType.InType],
  [AudienceFieldType.RegistrationUtmCampaignType]: [AudienceOpType.InType],
  [AudienceFieldType.RegistrationUtmMediumType]: [AudienceOpType.InType],
  [AudienceFieldType.RegistrationLinkType]: [AudienceOpType.InType],
  [AudienceFieldType.RegistrationEmailCampaignType]: [AudienceOpType.InType],
  [AudienceFieldType.EmailOpenedType]: [AudienceOpType.IsType],
  [AudienceFieldType.EmailClickedType]: [AudienceOpType.IsType],
  [AudienceFieldType.UpcomingStreamType]: [AudienceOpType.IsType],
};

export type ValueKind =
  | "bool"
  | "number"
  | "products"
  | "countries"
  | "languages"
  | "tags"
  | "links"
  | "emailCampaigns"
  | "emailNode"
  | "none";

const LIST_KINDS: ValueKind[] = [
  "products",
  "countries",
  "languages",
  "tags",
  "links",
  "emailCampaigns",
];

/** Какой редактор значения нужен условию. */
export function valueKind(
  field: AudienceFieldType,
  op: AudienceOpType
): ValueKind {
  // Открыл или кликнул письмо: да или нет плюс шаг письма цепочки.
  if (
    field === AudienceFieldType.EmailOpenedType ||
    field === AudienceFieldType.EmailClickedType
  ) {
    return "emailNode";
  }
  if (op === AudienceOpType.NeverType) return "none";
  if (op === AudienceOpType.IsType) return "bool";
  if (op === AudienceOpType.GteType || op === AudienceOpType.LteType) {
    return "number";
  }
  if (field === AudienceFieldType.PurchasedProductType) return "products";
  if (field === AudienceFieldType.CountryType) return "countries";
  if (field === AudienceFieldType.LanguageType) return "languages";
  if (field === AudienceFieldType.RegistrationLinkType) return "links";
  if (field === AudienceFieldType.RegistrationEmailCampaignType) {
    return "emailCampaigns";
  }
  return "tags";
}

export function defaultCondition(
  field: AudienceFieldType
): AudienceConditionInputType {
  const op = AUDIENCE_FIELD_OPS[field][0];
  const kind = valueKind(field, op);
  return {
    field,
    op,
    boolValue: kind === "bool" || kind === "emailNode" ? true : null,
    numberValue:
      kind === "number"
        ? field === AudienceFieldType.SpentUsdType
          ? 10
          : 30
        : null,
    stringValues: LIST_KINDS.includes(kind) ? [] : null,
    // Шаг письма только у полей письма: условие аудитории рассылки уходит
    // без ключа nodeId, как во входе фазы 3.
    ...(kind === "emailNode" ? { nodeId: null } : {}),
  };
}

/** Смена поля или операции сбрасывает значение под новый вид. */
export function changeCondition(
  condition: AudienceConditionInputType,
  patch: Partial<Pick<AudienceConditionInputType, "field" | "op">>
): AudienceConditionInputType {
  if (patch.field && patch.field !== condition.field) {
    return defaultCondition(patch.field);
  }
  if (patch.op && patch.op !== condition.op) {
    const kind = valueKind(condition.field, patch.op);
    return {
      ...condition,
      op: patch.op,
      numberValue: kind === "number" ? (condition.numberValue ?? 30) : null,
      stringValues:
        kind === "none" || kind === "number"
          ? null
          : (condition.stringValues ?? []),
    };
  }
  return condition;
}

/** Ключ перевода ошибки условия или null, если условие заполнено. */
export function conditionIssue(
  condition: AudienceConditionInputType,
  limits: MarketingLimits | null
): string | null {
  const kind = valueKind(condition.field, condition.op);
  if (kind === "emailNode" && !condition.nodeId) {
    return "automations.issues.CONDITION_EMAIL_NODE_INVALID";
  }
  if (
    kind === "number" &&
    (typeof condition.numberValue !== "number" ||
      !Number.isFinite(condition.numberValue) ||
      condition.numberValue < 0)
  ) {
    return "broadcasts.audience.errors.number";
  }
  if (kind === "number" && limits) {
    const max =
      condition.field === AudienceFieldType.SpentUsdType
        ? limits.audienceMaxNumber
        : limits.audienceMaxDays;
    if ((condition.numberValue ?? 0) > max) {
      return "broadcasts.audience.errors.tooLarge";
    }
  }
  if (LIST_KINDS.includes(kind)) {
    const count = (condition.stringValues ?? []).length;
    if (!count) return "broadcasts.audience.errors.values";
    if (limits && count > limits.audienceValues) {
      return "broadcasts.audience.errors.tooManyValues";
    }
  }
  return null;
}

export const audienceIsValid = (
  filter: AudienceFilterInputType | null,
  limits: MarketingLimits | null
): boolean =>
  !filter ||
  ((!limits || filter.conditions.length <= limits.audienceConditions) &&
    filter.conditions.every(
      (condition) => conditionIssue(condition, limits) === null
    ));

export const emptyAudience = (): AudienceFilterInputType => ({
  match: AudienceMatchType.AllType,
  conditions: [],
});
